import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuthHandlers } from "@/hooks/useAuthHandlers";
import { PasswordResetForm } from "@/components/PasswordResetForm";
import { ForgotPasswordForm } from "@/components/ForgotPasswordForm";
import { GuestOrWaitlistSection } from "@/components/GuestOrWaitlistSection";
import { toast } from "sonner";

// TODO: Security consideration - Users are auto-logged-in via invite/recovery links
// They can access the app without setting a password (password is only for future logins)
// Current behavior:
//   - New users: Receive email → click link → auto logged in → optional password setup
//   - Existing users: Receive recovery link → click link → auto logged in → optional password setup
// Consider adding a route guard to enforce password setup before app access
// For now, this is acceptable for beta - gets users in faster

const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [anonymousEnabled, setAnonymousEnabled] = useState<boolean | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [isInvite, setIsInvite] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  
  const {
    isLoading,
    isResending,
    needsVerification,
    handleSignUpWithConfirm,
    handleSignIn: signIn,
    handleResendVerification: resendVerification,
    handleForgotPassword,
    handleContinueAsGuest: continueAsGuest,
    checkAnonymousEnabled,
    validatePassword,
  } = useAuthHandlers();

  // Check if anonymous sign-ins are enabled and password reset/invite mode
  useEffect(() => {
    // Check if user is in password reset/invite mode
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get('type');
    const accessToken = hashParams.get('access_token');
    
    console.log('Auth page loaded. Hash params:', {
      type,
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!hashParams.get('refresh_token'),
      fullHash: window.location.hash
    });
    
    // Check if user is already authenticated (from recovery link)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session && type === 'recovery') {
        console.log('User already authenticated via recovery link');
        setShowResetPassword(true);
        setIsInvite(false);
        setUserEmail(session.user.email || null);
        return;
      }
      
      // If there's an access_token in the hash, they clicked a magic link or recovery link
      if (accessToken && (type === 'recovery' || type === 'magiclink' || type === 'invite')) {
        setShowResetPassword(true);
        setIsInvite(type === 'magiclink' || type === 'invite');
        
        // Exchange the token for a session first, then get user email
        const refreshToken = hashParams.get('refresh_token');
        
        if (!refreshToken) {
          console.error('No refresh token in URL');
          console.log('Available hash params:', Object.fromEntries(hashParams.entries()));
          toast.error('Invalid invite link. Please request a new one.');
          return;
        }
        
        supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        }).then(({ error: sessionError }) => {
          if (sessionError) {
            console.error('Error setting session:', sessionError);
            toast.error('Session error. Please try clicking the link again.');
            return;
          }
          return supabase.auth.getUser();
        }).then((result) => {
          if (!result) return; // Session error already handled
          const { data, error } = result;
          if (error) {
            console.error('Error getting user:', error);
            toast.error('Session error. Please try clicking the link again.');
            return;
          }
          if (data?.user?.email) {
            setUserEmail(data.user.email);
            console.log('User email loaded:', data.user.email);
          }
        });
      }
      
      // Only check anonymous enabled if we're not in password reset mode
      if (!session && !accessToken) {
        checkAnonymousEnabled().then(setAnonymousEnabled);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSignUpWithConfirm(email, password, confirmPassword);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    await signIn(email, password);
  };

  const handleResendVerification = async () => {
    await resendVerification(email);
  };

  const handleContinueAsGuest = async () => {
    await continueAsGuest(anonymousEnabled);
  };

  const handleForgotPasswordClick = async () => {
    if (email) {
      // If email is already entered, send reset directly
      await handleForgotPassword(email);
    } else {
      // Otherwise show the form
      setShowForgotPassword(true);
    }
  };

  const handleCancelForgotPassword = () => {
    setShowForgotPassword(false);
  };

  const handleResetPasswordSuccess = () => {
    setShowResetPassword(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-secondary px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">AI Conversation Editor</CardTitle>
          <CardDescription>
            Sign in to start editing your AI conversations
          </CardDescription>
        </CardHeader>
        <CardContent>
          {showResetPassword ? (
            <PasswordResetForm 
              onSuccess={handleResetPasswordSuccess}
              title={isInvite ? "Welcome! Set Your Password" : "Set New Password"}
              description={isInvite ? "You've been invited to join. Please set your password to get started." : "Enter your new password below."}
              submitButtonText={isInvite ? "Activate Account" : "Update Password"}
              userEmail={userEmail}
            />
          ) : showForgotPassword ? (
            <ForgotPasswordForm 
              onSuccess={() => setShowForgotPassword(false)}
              onCancel={handleCancelForgotPassword}
            />
          ) : (
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>
            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signin-email">Email</Label>
                  <Input
                    id="signin-email"
                    data-testid="email-input"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signin-password">Password</Label>
                  <Input
                    id="signin-password"
                    data-testid="password-input"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading} data-testid="login-button">
                  {isLoading ? "Signing in..." : "Sign In"}
                </Button>
                <Button 
                  type="button" 
                  variant="link" 
                  className="w-full text-sm" 
                  onClick={handleForgotPasswordClick}
                >
                  Forgot password?
                </Button>
                {needsVerification && (
                  <Button 
                    type="button" 
                    variant="ghost" 
                    className="w-full text-sm" 
                    onClick={handleResendVerification}
                    disabled={isResending || !email}
                  >
                    {isResending ? "Sending..." : "Resend verification email"}
                  </Button>
                )}
              </form>
            </TabsContent>
            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                  {password && !validatePassword(password).valid && (
                    <p className="text-sm text-destructive">{validatePassword(password).error}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-confirm-password">Confirm Password</Label>
                  <Input
                    id="signup-confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-sm text-destructive">Passwords do not match</p>
                  )}
                </div>
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isLoading || password !== confirmPassword || !validatePassword(password).valid}
                >
                  {isLoading ? "Creating account..." : "Create Account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          )}
          
          {!showForgotPassword && !showResetPassword && (
            <GuestOrWaitlistSection
              anonymousEnabled={anonymousEnabled}
              isLoading={isLoading}
              onContinue={handleContinueAsGuest}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
